import { useMemo, useRef, createContext, useContext } from 'react'

import Selecto, { SelectoEvents } from 'react-selecto'
import { useAppSelector } from 'shared/config/store'

import { getIsDragging } from '../model/selectors/dragSelectors'
import { getIsMultiSelectEnabled } from '../model/selectors/multiSelectSelectors'

type SelectoEvent = SelectoEvents['select']

// Create context for the selecto reference
export const SelectoRefContext = createContext<React.RefObject<any> | null>(null)

// Hook to access the selecto reference
export const useSelectoRef = () => useContext(SelectoRefContext)

type SelectoProps = {
  onSelect: (event: SelectoEvent, result: { id: string; targetClassName: string }[]) => void
  onSelectStart: () => void
  onSelectEnd: () => void
  selectedTargetClassNames?: string[]
  scrollRef?: React.RefObject<HTMLDivElement>
}

export const MultiSelectTool = ({
  onSelect,
  onSelectStart,
  onSelectEnd,
  selectedTargetClassNames = [],
  scrollRef,
}: SelectoProps) => {
  const selectoRef = useRef<any>(null)
  const isDragging = useAppSelector(getIsDragging)
  const isEnabled = useAppSelector(getIsMultiSelectEnabled)

  const selectableTargets = useMemo(
    () => selectedTargetClassNames.map((className) => `.${className}`),
    [selectedTargetClassNames]
  )

  const handleSelect = (event: SelectoEvent) => {
    const { selected } = event
    const result = selected.reduce<{ id: string; targetClassName: string }[]>((acc, item: HTMLElement) => {
      const targetClassName = selectedTargetClassNames.find((c) => item.classList.contains(c))
      if (targetClassName && item.id) acc.push({ id: item.id, targetClassName })
      return acc
    }, [])
    onSelect(event, result)
  }

  // Keep scroll options in sync with the actual scroll container
  const scrollOptions = useMemo(() => {
    const container = scrollRef?.current ?? document.body
    return {
      container,
      throttleTime: 30,
      threshold: 0,
      getScrollPosition: () => {
        const el = scrollRef?.current
        return el ? [el.scrollLeft, el.scrollTop] : [window.scrollX, window.scrollY]
      },
    }
  }, [scrollRef?.current])

  const containerEl = scrollRef?.current ?? document.body

  return (
    <SelectoRefContext.Provider value={selectoRef}>
      {!isDragging && isEnabled && (
        <Selecto
          ref={selectoRef}
          container={containerEl}
          dragContainer={containerEl}
          boundContainer={true}
          selectableTargets={selectableTargets}
          selectFromInside={true}
          hitRate={0}
          toggleContinueSelect={['shift']}
          selectByClick={false}
          clickBySelectEnd={true}
          scrollOptions={scrollOptions}
          onScroll={(e) => {
            const el = scrollRef?.current
            if (el) el.scrollBy(e.direction[0] * 10, e.direction[1] * 10)
          }}
          onSelectStart={onSelectStart}
          onSelectEnd={onSelectEnd}
          onSelect={handleSelect}
        />
      )}
    </SelectoRefContext.Provider>
  )
}
